import { Button } from "@dropbox/dig-components/buttons";
import { Skeleton } from "@dropbox/dig-components/skeleton";
import { Text } from "@dropbox/dig-components/typography";
import { Box, Split } from "@dropbox/dig-foundations";
import { tagged } from "@mirage/service-logging";
import { t } from "i18next";
import type { ReactNode } from "react";
import { ErrorBoundary } from "react-error-boundary";

import Sentry from "@mirage/shared/sentry";
import styles from "./OmniboxFallback.module.css";
import "./variables.module.css";

const logger = tagged("OmniboxErrorBoundary");

type OmniboxErrorBoundaryProps = {
  children: ReactNode;
};

const OmniboxFallback = () => {
  return (
    <Box padding="Micro Medium" className={styles.container}>
      <Skeleton.Box shouldAnimate height={30} />
      <Split
        gap={"small"}
        alignY={"center"}
        isFlexGap
        paddingY={"Micro Medium"}
      >
        <Split.Item style={{ marginRight: 8 }}>
          <Text>{t("omnibox_error")}</Text>
        </Split.Item>

        <Split.Item>
          <Button variant="primary" onClick={() => window.location.reload()}>
            {t("try_again")}
          </Button>
        </Split.Item>
      </Split>
    </Box>
  );
};

export function OmniboxErrorBoundary({ children }: OmniboxErrorBoundaryProps) {
  const handleOnError = (error: Error) => {
    logger.error("Error in Omnibox component", error);

    Sentry.withScope((scope) => {
      Sentry.captureMessage(
        "[OmniboxErrorBound] Error thrown from omnibox",
        "error",
        {},
        // Ensure scope is passed in here
        scope,
      );
    });
  };

  return (
    <ErrorBoundary FallbackComponent={OmniboxFallback} onError={handleOnError}>
      {children}
    </ErrorBoundary>
  );
}
